import type { VenueMapCamera } from "./venueMapProjection";

export const VENUE_MAP_ROOF_TINT = 0.16;
export const VENUE_MAP_RIM_TINT = 0.28;
export const VENUE_MAP_WALL_AMBIENT = 0.76;
export const VENUE_MAP_WALL_DIFFUSE = 0.22;

export type VenueMapLightDirection = { x: number; y: number; z: number };

/**
 * A broad headlight centred on the viewing axis. Keeping it directional avoids
 * moving the lighting when panning or zooming. Only roof and wall shading are
 * rendered; no cast-shadow geometry is generated.
 */
export function venueMapLighting(
  camera: Pick<VenueMapCamera, "rotation" | "pitch">,
) {
  const rotation = Number.isFinite(camera.rotation)
    ? ((camera.rotation % 360) + 360) % 360
    : 0;
  const pitch = Number.isFinite(camera.pitch)
    ? Math.min(70, Math.max(0, camera.pitch))
    : 0;
  const yaw = (rotation * Math.PI) / 180;
  const tilt = (pitch * Math.PI) / 180;
  const clean = (value: number) => (Math.abs(value) < 1e-12 ? 0 : value);
  // In camera coordinates +Y points towards the viewer on the floor and +Z
  // points upwards. Inverse yaw brings that viewing axis back into the map.
  const direction = {
    x: clean(Math.sin(yaw) * Math.sin(tilt)),
    y: clean(Math.cos(yaw) * Math.sin(tilt)),
    z: Math.cos(tilt),
  };
  return { direction };
}

/** Soft ambient fill keeps sides legible; the camera-facing side is brighter. */
export function venueMapWallLight(
  normal: { x: number; y: number },
  direction: VenueMapLightDirection,
) {
  const length = Math.hypot(normal.x, normal.y);
  if (!Number.isFinite(length) || length === 0) return VENUE_MAP_WALL_AMBIENT;
  const diffuse = Math.max(
    0,
    (normal.x * direction.x + normal.y * direction.y) / length,
  );
  return VENUE_MAP_WALL_AMBIENT + VENUE_MAP_WALL_DIFFUSE * diffuse;
}
